import React, { useState, useEffect, useCallback } from 'react';
import { usePlaidLink } from 'react-plaid-link';
import styled from 'styled-components';
import Button from '../Button';
import { BodyLarge } from '../Texts';
import LoadingSpinner from '../LoadingSpinner';
import {
  useCreatePlaidLinkTokenMutation,
  useExchangePlaidPublicTokenMutation,
  useGetExternalAccountsQuery,
  useGetLiquidationAddressesQuery,
} from '../../redux/api/authorized/bridge';

const PlaidContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: 16px 0;
`;

const StatusMessage = styled(BodyLarge)`
  text-align: center;
  color: ${({ theme }) => theme.text.medium};
  padding: 16px;
  background: ${({ theme }) => theme.background.light};
  border-radius: 8px;
`;

const SuccessMessage = styled(BodyLarge)`
  text-align: center;
  color: ${({ theme }) => theme.status.success.primary};
  padding: 16px;
  background: ${({ theme }) => theme.primary.background};
  border: 1px solid ${({ theme }) => theme.status.success.primary};
  border-radius: 8px;
`;

const ErrorMessage = styled(BodyLarge)`
  text-align: center;
  color: ${({ theme }) => theme.status.error.primary};
  padding: 16px;
  background: ${({ theme }) => theme.status.error.background};
  border: 1px solid ${({ theme }) => theme.status.error.primary};
  border-radius: 8px;
`;

const PlaidLink = ({ onSuccess, onError }) => {
  const [createPlaidLinkToken] = useCreatePlaidLinkTokenMutation();
  const [exchangePlaidPublicToken] = useExchangePlaidPublicTokenMutation();

  const [linkToken, setLinkToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [exchanging, setExchanging] = useState(false);
  const [checkingAccounts, setCheckingAccounts] = useState(false);

  // Polling for accounts after successful exchange
  const {
    data: externalAccounts,
    refetch: refetchExternalAccounts,
    isLoading: externalAccountsLoading,
  } = useGetExternalAccountsQuery(undefined, {
    skip: !success,
    pollingInterval: success && checkingAccounts ? 5000 : 0,
  });

  const {
    data: liquidationAddresses,
    refetch: refetchLiquidationAddresses,
    isLoading: liquidationAddressesLoading,
  } = useGetLiquidationAddressesQuery(undefined, {
    skip: !success,
    pollingInterval: success && checkingAccounts ? 5000 : 0,
  });

  // Handle successful Plaid Link
  const handleOnSuccess = useCallback(
    async (public_token, metadata) => {
      try {
        setExchanging(true);
        setError(null);

        const response = await exchangePlaidPublicToken({
          link_token_id: linkToken,
          public_token: public_token,
        }).unwrap();

        setSuccess(true);
        setExchanging(false);
        setCheckingAccounts(true);

        // Start checking for accounts after 30 seconds
        setTimeout(() => {
          refetchExternalAccounts();
          refetchLiquidationAddresses();
        }, 30000);
      } catch (error) {
        setError('Failed to connect bank account. Please try again.');
        setExchanging(false);
        if (onError) onError(error);
      }
    },
    [
      linkToken,
      exchangePlaidPublicToken,
      refetchExternalAccounts,
      refetchLiquidationAddresses,
      onError,
    ],
  );

  // Handle Plaid Link errors
  const handleOnExit = useCallback(
    (err, metadata) => {
      if (err) {
        setError('Bank connection was cancelled or failed.');
        if (onError) onError(err);
      }
    },
    [onError],
  );

  // Add error handling for Plaid Link initialization
  const handleOnLoad = useCallback(() => {
    console.log('Plaid Link loaded successfully');
  }, []);

  const handleOnEvent = useCallback((eventName, metadata) => {
    console.log('Plaid Link event:', eventName, metadata);
  }, []);

  // Initialize Plaid Link with enhanced error handling
  const {
    open,
    ready,
    error: plaidError,
  } = usePlaidLink({
    token: linkToken,
    onSuccess: handleOnSuccess,
    onExit: handleOnExit,
    onLoad: handleOnLoad,
    onEvent: handleOnEvent,
  });

  // Handle Plaid SDK errors
  useEffect(() => {
    if (plaidError) {
      console.error('Plaid Link error:', plaidError);
      setError(
        `Plaid initialization failed: ${plaidError.message || 'Unknown error'}`,
      );
    }
  }, [plaidError]);

  // Auto-open Plaid Link when ready
  useEffect(() => {
    if (ready && linkToken && !exchanging && !success) {
      console.log('Opening Plaid Link with token:', linkToken);
      open();
    }
  }, [ready, linkToken, open, exchanging, success]);

  // Create link token
  const handleStartPlaidFlow = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await createPlaidLinkToken().unwrap();
      console.log('Plaid link token response:', response);

      if (!response.link_token) {
        throw new Error('No link_token received from server');
      }

      setLinkToken(response.link_token);
      setLoading(false);
    } catch (error) {
      console.error('Failed to create Plaid link token:', error);
      setError(
        `Failed to initialize bank connection: ${error.message || 'Unknown error'}. Please try again.`,
      );
      setLoading(false);
      if (onError) onError(error);
    }
  };

  // Check if accounts were created successfully
  useEffect(() => {
    if (
      success &&
      checkingAccounts &&
      externalAccounts &&
      liquidationAddresses
    ) {
      if (externalAccounts.length > 0 || liquidationAddresses.length > 0) {
        setCheckingAccounts(false);
        if (onSuccess) {
          onSuccess({
            external_accounts: externalAccounts,
            liquidation_addresses: liquidationAddresses,
          });
        }
      }
    }
  }, [
    success,
    checkingAccounts,
    externalAccounts,
    liquidationAddresses,
    onSuccess,
  ]);

  return (
    <PlaidContainer>
      {!linkToken && !loading && !error && (
        <>
          <BodyLarge>
            Connect your bank account instantly with Plaid for faster setup.
          </BodyLarge>
          <Button onClick={handleStartPlaidFlow} fullWidth={true}>
            Connect Bank Account
          </Button>
        </>
      )}

      {loading && (
        <StatusMessage>
          <LoadingSpinner />
          <div style={{ marginTop: '8px' }}>Preparing bank connection...</div>
        </StatusMessage>
      )}

      {exchanging && (
        <StatusMessage>
          <LoadingSpinner />
          <div style={{ marginTop: '8px' }}>
            Connecting your bank account...
          </div>
        </StatusMessage>
      )}

      {success && checkingAccounts && (
        <StatusMessage>
          <LoadingSpinner />
          <div style={{ marginTop: '8px' }}>
            Setting up your liquidation addresses... This may take up to a
            minute.
          </div>
        </StatusMessage>
      )}

      {success && !checkingAccounts && (
        <SuccessMessage>
          ✅ Bank account connected successfully! Your liquidation addresses are
          ready.
        </SuccessMessage>
      )}

      {error && (
        <>
          <ErrorMessage>
            {error}
            {linkToken && (
              <div
                style={{
                  marginTop: '8px',
                  fontSize: '12px',
                  fontFamily: 'monospace',
                  wordBreak: 'break-all',
                }}
              >
                Debug: Token starts with: {linkToken.substring(0, 20)}...
              </div>
            )}
          </ErrorMessage>
          <Button
            onClick={handleStartPlaidFlow}
            variant='secondary'
            fullWidth={true}
          >
            Try Again
          </Button>
        </>
      )}
    </PlaidContainer>
  );
};

export default PlaidLink;
